import React, { useState, useMemo } from "react";

// Best-effort merchant → domain map. Falls back to lowercased-slug + ".com".
const KNOWN_DOMAINS = {
    netflix: "netflix.com",
    spotify: "spotify.com",
    hulu: "hulu.com",
    disney: "disneyplus.com",
    "disney+": "disneyplus.com",
    "amazon prime": "amazon.com",
    amazon: "amazon.com",
    "amazon prime video": "amazon.com",
    uber: "uber.com",
    "uber eats": "ubereats.com",
    lyft: "lyft.com",
    doordash: "doordash.com",
    grubhub: "grubhub.com",
    starbucks: "starbucks.com",
    "trader joes": "traderjoes.com",
    "trader joe's": "traderjoes.com",
    walmart: "walmart.com",
    target: "target.com",
    costco: "costco.com",
    apple: "apple.com",
    "apple music": "apple.com",
    icloud: "apple.com",
    "apple icloud": "apple.com",
    google: "google.com",
    "google one": "google.com",
    youtube: "youtube.com",
    "youtube premium": "youtube.com",
    microsoft: "microsoft.com",
    xbox: "xbox.com",
    playstation: "playstation.com",
    nintendo: "nintendo.com",
    adobe: "adobe.com",
    dropbox: "dropbox.com",
    notion: "notion.so",
    figma: "figma.com",
    linkedin: "linkedin.com",
    "linkedin premium": "linkedin.com",
    twitch: "twitch.tv",
    hbo: "max.com",
    max: "max.com",
    paramount: "paramountplus.com",
    peacock: "peacocktv.com",
    "att": "att.com",
    "at&t": "att.com",
    verizon: "verizon.com",
    tmobile: "t-mobile.com",
    "t-mobile": "t-mobile.com",
    xfinity: "xfinity.com",
    comcast: "xfinity.com",
    spectrum: "spectrum.com",
    geico: "geico.com",
    progressive: "progressive.com",
    statefarm: "statefarm.com",
    "state farm": "statefarm.com",
    allstate: "allstate.com",
    airbnb: "airbnb.com",
    booking: "booking.com",
    delta: "delta.com",
    united: "united.com",
    "american airlines": "aa.com",
    southwest: "southwest.com",
    chase: "chase.com",
    "bank of america": "bankofamerica.com",
    wellsfargo: "wellsfargo.com",
    "wells fargo": "wellsfargo.com",
    citi: "citi.com",
    capitalone: "capitalone.com",
    "capital one": "capitalone.com",
    amex: "americanexpress.com",
    "american express": "americanexpress.com",
    discover: "discover.com",
    venmo: "venmo.com",
    zelle: "zellepay.com",
    paypal: "paypal.com",
    stripe: "stripe.com",
    robinhood: "robinhood.com",
    fidelity: "fidelity.com",
    schwab: "schwab.com",
    vanguard: "vanguard.com",
    coinbase: "coinbase.com",
    binance: "binance.com",
    tesla: "tesla.com",
    shell: "shell.com",
    chevron: "chevron.com",
    exxon: "exxon.com",
    mcdonalds: "mcdonalds.com",
    "mcdonald's": "mcdonalds.com",
    chipotle: "chipotle.com",
    subway: "subway.com",
    kfc: "kfc.com",
    dominos: "dominos.com",
    "domino's": "dominos.com",
    peloton: "onepeloton.com",
    audible: "audible.com",
    nyt: "nytimes.com",
    "new york times": "nytimes.com",
    "wall street journal": "wsj.com",
    wsj: "wsj.com",
};

// PALETTE for the fallback avatar — deterministic by merchant name.
const PALETTE = [
    ["#EF4444", "#FCA5A5"], ["#F97316", "#FDBA74"], ["#EAB308", "#FDE68A"],
    ["#22C55E", "#86EFAC"], ["#10B981", "#6EE7B7"], ["#06B6D4", "#67E8F9"],
    ["#3B82F6", "#93C5FD"], ["#6366F1", "#A5B4FC"], ["#8B5CF6", "#C4B5FD"],
    ["#EC4899", "#F9A8D4"],
];

const hashCode = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
};

const guessDomain = (merchant) => {
    if (!merchant) return null;
    const key = merchant.trim().toLowerCase();
    if (KNOWN_DOMAINS[key]) return KNOWN_DOMAINS[key];
    // Try to strip common suffixes and slug it.
    const slug = key
        .replace(/[®™©]/g, "")
        .replace(/\b(inc|llc|ltd|corp|co|the)\b/g, "")
        .replace(/[^a-z0-9]+/g, "")
        .trim();
    if (!slug) return null;
    return `${slug}.com`;
};

/**
 * MerchantLogo — Clearbit-backed logo with a graceful color-avatar fallback
 * (initials on a colored circle) when the CDN 404s or the merchant is unknown.
 * Rocket Money uses this exact pattern to make every transaction feel branded.
 */
export const MerchantLogo = ({ merchant, size = 36, className = "" }) => {
    const domain = useMemo(() => guessDomain(merchant), [merchant]);
    const [errored, setErrored] = useState(false);

    const initials = (merchant || "?")
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() || "")
        .join("");

    const [bg, fg] = PALETTE[hashCode(merchant || "") % PALETTE.length];
    const style = { width: size, height: size };

    if (domain && !errored) {
        return (
            <img
                src={`https://logo.clearbit.com/${domain}`}
                alt={merchant}
                onError={() => setErrored(true)}
                style={style}
                className={`rounded-xl object-cover bg-white ${className}`}
                data-testid="merchant-logo"
            />
        );
    }

    return (
        <div
            style={{ ...style, background: bg, color: fg }}
            className={`rounded-xl grid place-items-center font-black text-xs shrink-0 ${className}`}
            data-testid="merchant-logo-fallback"
        >
            {initials || "?"}
        </div>
    );
};

export default MerchantLogo;
